import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  CheckCircle2,
  Star,
  Activity,
  Calendar,
  Sparkles,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import DesignModeToggle from "../components/terminal/DesignModeToggle";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import ShockGamesCarousel from "../components/ShockGamesCarousel";

/**
 * Anonymous landing / marketing page (Iter 83 hard-wall).
 *
 * Renders for any visitor without a JWT.  No predictions are
 * disclosed — only marketing copy + signup CTAs.  Authenticated
 * users get the real homepage instead via the App.js gate.
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav (minimal — no app links yet).
          Iter 145t — Mobile compact mode: hide subtitle + "Log in" text-button
          on <sm so the bar fits a 360-393px iPhone width without wrapping. */}
      <header className="border-b border-border sticky top-0 z-40 bg-background/85 backdrop-blur-xl pt-safe">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg bg-foreground text-background grid place-items-center font-display font-bold text-sm shrink-0">
              N
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold tracking-tight truncate text-sm sm:text-base">NDAKUBET</div>
              <div className="hidden sm:block text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                AI football intel
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <LanguageSwitcher compact />
            <button
              onClick={toggleTheme}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors px-2 sm:px-3 py-1.5 min-touch sm:min-h-0 sm:min-w-0"
              data-testid="landing-theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? "☀" : "☾"}
            </button>
            <DesignModeToggle />
            <button
              onClick={() => navigate("/auth")}
              className="hidden sm:inline-flex text-sm font-semibold px-4 py-2 rounded-full hover:bg-muted transition-colors"
              data-testid="landing-login-btn"
            >
              {t("common.login")}
            </button>
            <button
              onClick={() => navigate("/auth?mode=register")}
              className="text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity whitespace-nowrap"
              data-testid="landing-signup-btn"
            >
              {t("common.getStarted")}
            </button>
          </div>
        </div>
      </header>

      <ShockGamesCarousel />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-amber-400/15 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 h-[26rem] w-[26rem] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.16em] uppercase text-amber-600 dark:text-amber-400 mb-5">
                <Sparkles size={14} className="fill-amber-400 text-amber-400" />
                <span>{t("landing.eyebrow")}</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                {t("landing.headlinePart1")}
                <br />
                <span className="text-amber-600 dark:text-amber-400">{t("landing.headlineHighlight")}</span> {t("landing.headlinePart2")}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                {t("landing.subhead")}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate("/auth?mode=register")}
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background font-bold text-sm hover:opacity-90 transition-opacity shadow-lg"
                  data-testid="landing-hero-signup"
                >
                  {t("common.createAccount")}
                  <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  onClick={() => navigate("/auth")}
                  className="text-sm font-semibold px-5 py-3 rounded-full border border-border hover:bg-muted transition-colors"
                  data-testid="landing-hero-login"
                >
                  {t("common.iHaveAccount")}
                </button>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 size={13} className="text-emerald-500" />
                  {t("common.free")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 size={13} className="text-emerald-500" />
                  {t("common.cancelAnytime")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 size={13} className="text-emerald-500" />
                  {t("common.responsibleGambling")}
                </span>
              </div>
            </div>

            {/* Sample card mockup */}
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl border border-border bg-card/95 backdrop-blur-sm p-6 shadow-2xl">
                <div className="absolute -top-3 -right-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500 text-emerald-50 text-[10px] font-bold tracking-[0.16em] uppercase shadow-md">
                  ● {t("landing.samplePick")}
                </div>
                <div className="flex items-center justify-between text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground mb-3">
                  <span>🇬🇧 Premier League</span>
                  <span className="font-tabular">SAT 16:30</span>
                </div>
                <div className="font-display text-lg sm:text-xl font-bold leading-snug mb-3">
                  Manchester City <span className="text-muted-foreground font-normal">vs</span> Brentford
                </div>
                <div className="rounded-2xl bg-muted/40 border border-border p-4">
                  <div className="flex items-center justify-between text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground mb-2">
                    <span>HT Both teams to score</span>
                    <span className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          size={11}
                          className={
                            n <= 4
                              ? "fill-amber-400 text-amber-400"
                              : "fill-transparent text-amber-400/30"
                          }
                        />
                      ))}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="font-display font-bold text-2xl">No</span>
                    <span className="font-tabular font-bold text-2xl text-amber-600 dark:text-amber-400">
                      85%
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full bg-background rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400" style={{ width: "85%" }} />
                  </div>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  Trust 4★ · league hit-rate 70% (n=180) · calibrated bucket gap 4.2pp
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              {t("landing.membersAreaTitle")}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {t("landing.membersAreaSubtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              Icon={Star}
              title={t("landing.features.trust")}
              copy={t("landing.features.trustDesc")}
            />
            <FeatureCard
              Icon={Activity}
              title={t("landing.features.live")}
              copy={t("landing.features.liveDesc")}
            />
            <FeatureCard
              Icon={Calendar}
              title={t("landing.features.weekend")}
              copy={t("landing.features.weekendDesc")}
            />
            <FeatureCard
              Icon={TrendingUp}
              title={t("landing.features.selfLearning")}
              copy={t("landing.features.selfLearningDesc")}
            />
            <FeatureCard
              Icon={ShieldCheck}
              title={t("landing.features.slips")}
              copy={t("landing.features.slipsDesc")}
            />
            <FeatureCard
              Icon={Sparkles}
              title={t("landing.features.betway")}
              copy={t("landing.features.betwayDesc")}
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            {t("landing.ctaTitle")}
          </h2>
          <p className="mt-3 text-muted-foreground">
            {t("landing.ctaSubtitle")}
          </p>
          <button
            onClick={() => navigate("/auth?mode=register")}
            className="mt-7 group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-foreground text-background font-bold text-sm hover:opacity-90 transition-opacity shadow-lg"
            data-testid="landing-final-signup"
          >
            {t("common.createAccount")}
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
          <p className="mt-4 text-xs text-muted-foreground">
            <button
              onClick={() => navigate("/auth")}
              className="underline hover:text-foreground transition-colors"
              data-testid="landing-final-login"
            >
              {t("common.iHaveAccount")}
            </button>
          </p>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} NDAKUBET · Data-driven football intelligence
          </p>
          <p className="text-[11px] text-muted-foreground max-w-xl">
            Predictions are for informational purposes only. 18+. Please gamble responsibly.
          </p>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ Icon, title, copy }) => (
  <div className="rounded-2xl border border-border bg-card p-6 hover:border-amber-400/40 transition-colors">
    <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-amber-400/15 text-amber-600 dark:text-amber-400 mb-4">
      <Icon size={18} />
    </div>
    <h3 className="font-display font-bold text-lg tracking-tight mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{copy}</p>
  </div>
);

export default LandingPage;
